/**
 * Hiérarchie des statuts d'application d'un champ personnalisé.
 * L'ordre définit quelle "étape" de la vie du ticket déclenche l'affichage du champ.
 */
export const FIELD_STATUS_HIERARCHY = ['open', 'in_progress', 'closed', 'approved'];

/**
 * Mapping statut ticket (API) → field_apply_status (champ).
 */
export const TICKET_TO_FIELD_STATUS = {
  open:        'open',
  in_progress: 'in_progress',
  closed:      'closed',
  approved:    'approved',
};

/**
 * Aplatie récursivement la hiérarchie parent → child_field en une liste plate.
 * Chaque nœud conserve une référence `_parentId` pour le rendu conditionnel.
 *
 * L'API retourne uniquement les champs racines dans /client/{id}/champs,
 * les enfants étant imbriqués dans `child_field`.
 */
function flattenChamps(champs) {
  const result = [];
  function visit(champ, parentId = null) {
    result.push({ ...champ, _parentId: parentId });
    if (champ.child_field) {
      visit(champ.child_field, champ.id);
    }
  }
  champs.forEach(c => visit(c, null));
  return result;
}

/**
 * Retourne les champs à afficher lors d'une transition de statut,
 * triés par position, avec la hiérarchie parent/enfant aplatie.
 *
 * @param {Array}       allChamps        - Champs racines du client (/client/{id}/champs)
 * @param {string|null} fromFieldStatus  - Statut de départ (null = création)
 * @param {string}      toFieldStatus    - Statut cible
 */
export function getApplicableChamps(allChamps, fromFieldStatus, toFieldStatus) {
  if (!Array.isArray(allChamps) || allChamps.length === 0) return [];

  const fromIdx = fromFieldStatus ? FIELD_STATUS_HIERARCHY.indexOf(fromFieldStatus) : -1;
  const toIdx   = FIELD_STATUS_HIERARCHY.indexOf(toFieldStatus);
  if (toIdx === -1) return [];

  const flat = flattenChamps(allChamps);

  return flat
    .filter(champ => {
      const fieldIdx = FIELD_STATUS_HIERARCHY.indexOf(champ.field_apply_status);
      return fieldIdx !== -1 && fieldIdx > fromIdx && fieldIdx <= toIdx;
    })
    .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
}

/**
 * Détermine si un champ enfant doit être visible en fonction de la valeur
 * actuelle de son parent et de la visibility_rule du champ enfant.
 *
 * La visibility_rule est la valeur que le parent doit avoir pour que
 * l'enfant soit affiché.
 *
 * @param {object} champ          - Champ enfant (avec _parentId et visibility_rule)
 * @param {object} fieldValues    - Map { champId: valeur }
 * @returns {boolean}
 */
export function isChampVisible(champ, fieldValues) {
  // Champ racine → toujours visible
  if (!champ._parentId) return true;

  const parentValue = fieldValues[champ._parentId];
  if (!champ.visibility_rule) return true; // pas de règle → toujours visible

  return String(parentValue) === String(champ.visibility_rule);
}
